import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { RefreshControl, BackHandler, View, Linking, AppState, FlatList } from 'react-native';
import PropTypes from 'prop-types';
import Theme from 'Theme';
import {
    Button,
    Carousel,
    PromoCarousel,
    withConfirmationModal,
    Wizard,
    Footer
} from 'LocalGluons';
import NavigationService from 'NavigationService';
import { ITEM_TYPES } from 'Utils';
import { Device, getActualHeight, Orientation, WEB_WIDTH } from 'DeviceUtils';
import { useStore } from 'Store';
import { Playlist } from '@24i/st-api-connector';
import { PageItem } from 'Pages/Page/PageItem';
import { logEvent } from 'AppManager';
import { useConfirmationModal } from 'LocalGluons/components/ConfirmationModal';
import { t } from 'Locale';
import { handleScroll, show, setActiveMenuItem, isMinimised } from 'ReduxStore/webMenu';
import { useAppSelector, useAppDispatch } from 'ReduxStore/hooks';
import { useNavigation, history } from '@react-navigation/native';
import { Freeze } from 'react-freeze';
import { FlashList } from '@shopify/flash-list';
import {
    DETAIL_HEADER_HEIGHT,
    TOP_BAR_HEIGHT,
    TOP_BAR_ITEMS_HEIGHT,
    TOP_BAR_TOTAL_HEIGHT
} from '../../../runtime/Navigation/Menu/Menu.styles';
import { PageItemTypeEnum } from '../../../options';
import ContentWrapper from '../ContentWrapper';
import ConsentsIntroPage from '../NativeConsents/NativeConsentsIntro';
import defaultStyles from './Page.jss';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';

// eslint-disable-next-line max-statements
const Page = ({ navigation, route }) => {
    const { appManager, userData, packageFilter, setPackageFilter, mCoreConfig, mySubscriptions } =
        useStore();
    const [loaded, setLoaded] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [pageItems, setPageItems] = useState([]);
    const userDataRef = useRef(userData);
    const [pageOptions, setPageOption] = useState({});
    const navDidBlurListenerRef = useRef(() => {});
    const navWillFocusListenerRef = useRef(() => {});
    const setTimeoutBlur = useRef(null);
    const [pageIsBlurred, setPageIsBlurred] = useState(false);
    const [filterOptions, setFilterOptions] = useState([]);
    const [canalPlusAppInstalled, setCanalPlusAppInstalled] = useState(null);
    const [canalPlusWizard, setCanalPlusWizard] = useState(null);
    const [showConsentsAgain, setShowConsentsAgain] = useState(false);
    const appState = useRef(AppState.currentState);
    const { navigate, updateNavigationProps, navigationProps } = useNavigationPropsContext();
    const { setModalContent } = useConfirmationModal();
    const navHandler = useNavigation();
    const dispatch = useAppDispatch();
    const [contentHeight, setContentHeight] = useState(0);
    const windowHeight = getActualHeight();
    const isLongContent = contentHeight > windowHeight;
    const bottomPadding = Math.max(windowHeight - contentHeight, 0);

    const checkCanalPlusApp = useCallback(async () => {
        const {
            thirdPartyAppIds: { canalplus }
        } = mCoreConfig;
        try {
            const isCanalPlusInstalled = await Linking.canOpenURL(canalplus);
            setCanalPlusAppInstalled(isCanalPlusInstalled);
        } catch (error) {
            setCanalPlusAppInstalled(false);
        }
    }, [setCanalPlusAppInstalled]);

    const getCanalPlus = useCallback(async () => {
        const {
            backstageConnector: { wizard }
        } = appManager.getApi();
        try {
            const wizardRes = await wizard.fetchWizard('canalplus');
            setCanalPlusWizard(wizardRes);
        } catch (error) {
            setCanalPlusWizard('error');
        }
    }, [setCanalPlusAppInstalled]);

    const initializePage = async (isRefreshing = false) => {
        NavigationService.setAppNavigationInstance(navigation);
        setPageOption(route.params?.pageOptions || null);

        // dispatch(show());
        if (isRefreshing) {
            setRefreshing(isRefreshing);
        } else {
            setLoaded(false);
        }
        try {
            const { items = [] } = await appManager.fetchPageData(route.params?.reference);
            const newPageItems = items.map((item) => new PageItem(item));
            const promises = newPageItems.map((pageItem, index) =>
                pageItem.downloadCarouselData(index)
            );
            await Promise.all(promises.map((promise) => promise.then()));
            setPageItems(newPageItems);
            if (mCoreConfig && mCoreConfig.hideVod !== undefined && !mCoreConfig.hideVod) {
                const packagesResponse = await appManager.api.fetchPackages();
                const filterOpts = packagesResponse?.items;
                setFilterOptions(filterOpts);
            }
        } catch (error) {
            console.log('USER NOT LOGED', error);
            dispatch(setActiveMenuItem('watch-tv'));
            // navigate('watch-tv');
            navigate('Main', { screen: 'watch-tv' });
        } finally {
            if (isRefreshing) {
                setRefreshing(!isRefreshing);
            } else {
                setLoaded(true);

                // dispatch(setLoadedRedux());
            }
        }
    };

    const hideFilter = () => {
        updateNavigationProps({ displayPageFilter: false });
        // NavigationService.getAppNavigationInstance().setParams({ displayPageFilter: false });
    };

    useEffect(() => {
        initializePage();
        let timeout;
        setTimeoutBlur.current = navDidBlurListenerRef.current = navigation.addListener(
            'blur',
            () => {
                setTimeout(() => {
                    setPageIsBlurred(true);
                }, 1000);
                if (route.params?.pageOptions?.slug === 'special_canalplus_1') {
                    setCanalPlusAppInstalled(null);
                }
                hideFilter();
            }
        );

        navWillFocusListenerRef.current = navigation.addListener('focus', () => {
            clearTimeout(setTimeoutBlur.current);
            setPageIsBlurred(false);
            NavigationService.setAppNavigationInstance(navigation);
            if (route.params?.pageOptions?.slug === 'special_canalplus_1') {
                checkCanalPlusApp();
            }
        });

        const subscription = AppState.addEventListener('change', (nextAppState) => {
            if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
                checkCanalPlusApp();
            }

            appState.current = nextAppState;
        });

        return () => {
            clearTimeout(timeout);
            subscription.remove();
            navDidBlurListenerRef?.current();
            navWillFocusListenerRef?.current();
            // Ensure portrait lock
            if (Device.isPhone) Orientation.lockToPortrait();
        };
    }, []);

    useEffect(() => {
        const checkConsents = async () => {
            try {
                const {
                    backstageConnector: { auth }
                } = appManager.getApi();
                const consentsResponse = await auth.getNativeConsents();
                if (userData.showNativeConsentsScreen) {
                    setModalContent(
                        <ConsentsIntroPage
                            navigation={navigation}
                            setModalContent={setModalContent}
                            consentsResponse={consentsResponse}
                            setShowConsentsAgain={setShowConsentsAgain}
                        />,
                        handleBackButton
                    );
                }
            } catch (error) {
                console.log(error);
            }
        };
        if (mCoreConfig && mCoreConfig.nativeConsentsEnabled) {
            checkConsents();
            setShowConsentsAgain(false);
        }
    }, [showConsentsAgain]);

    const handleBackButton = () => {
        BackHandler.exitApp();
        return true;
    };

    // Refresh page carousels which has update flag on new focus
    useEffect(() => {
        const updateItems = async () => {
            await Promise.all(
                pageItems.map((pageItem, index) => {
                    if (pageItem.carouselData.updateOnPageFocus) {
                        return pageItem.downloadCarouselData(index);
                    }
                    return null;
                })
            );
            setPageItems([...pageItems]);
        };
        if (!pageIsBlurred) {
            updateItems();
        }
    }, [pageIsBlurred]);

    // On user login/logout/change reload whole page
    useEffect(() => {
        if ((!userDataRef.current && userData) || userDataRef.current?.login !== userData?.login) {
            initializePage();
        }
    }, [userData]);

    // handle refreshing live and next carousels
    useEffect(() => {
        const filterTvPages = (pageItem) =>
            pageItem.getCarouselData().dataType === ITEM_TYPES.TV &&
            pageItem.getCarouselData().items;
        const tvCarousels = pageItems.filter(filterTvPages);
        const tvItemEndTimesByCarousel = tvCarousels.map((carousel) =>
            carousel.getCarouselData().items.map((item) => item.data.end)
        );
        const tvItemEndTimes = tvItemEndTimesByCarousel.reduce(
            (total, curr) => [...total, ...curr],
            []
        );
        const nearestEndTime = Math.min(...tvItemEndTimes);
        // delay the refresh by 1 second to make sure data is updated on backend
        const refreshTime = nearestEndTime - Date.now() + 5000;

        if (!Number.isFinite(refreshTime) || pageIsBlurred || refreshTime < 0) return () => {};
        const refreshTvPages = async () => {
            const newItems = [...pageItems];
            await Promise.all(
                newItems
                    .filter(filterTvPages)
                    .map((page, index) => page.downloadCarouselData(index))
            );
            setPageItems(newItems);
        };
        const timer = setTimeout(refreshTvPages, refreshTime);

        return () => {
            clearTimeout(timer);
        };
    }, [pageItems, pageIsBlurred]);

    const renderItem = useCallback(({ item }) => {
        if (item && Device.isWeb) {
            return <View style={{ width: WEB_WIDTH, alignSelf: 'center' }}>{item}</View>;
        }
        if (item) {
            return item;
        }
        return null;
    }, []);

    const FooterComponent = useMemo(
        () => (
            <View style={{ width: '100%' }}>
                <Footer />
            </View>
        ),
        []
    );

    const onRefresh = () => {
        initializePage(true);
    };

    const onViewAllPress = (title, showMetadata, playlistUrl) => {
        navigate('ViewAll', { title, showMetadata, playlistUrl });
    };

    const onItemPress = (item) => {
        dispatch(show());
        const { originalModelType, modelType, data } = item;
        const type = originalModelType || modelType;

        const id =
            data?.type === ITEM_TYPES.PVR
                ? data.scheduleId
                : type === ITEM_TYPES.RECORDING
                  ? data.programId
                  : data.id;

        const platform = data?.pckg?.platform;

        navigate('Details', { item }, { id, type, platform });
    };

    const onPackageFilterSelect = (item) => {
        setPackageFilter(item);
        // getting parent navigation because that's the one with access to topBar
        navigation.getParent().setParams({
            filterActive: item !== 0
        });
        hideFilter();
    };

    /// If at least one promo slider is initialized and page is ''home''
    const showTvGuideButton =
        route.name === 'home' &&
        pageItems.find(
            (pageItem) => pageItem.getCarouselData() && pageItem.type === PageItemTypeEnum.SLIDER
        );

    const getCarouselData = (pageItemsData) => {
        const promoCarouselsData = [];
        const singleRowCarouselsData = [];

        pageItemsData.forEach((pageItem) => {
            const carouselData = pageItem.getCarouselData();
            if (carouselData) {
                if (carouselData.type === PageItemTypeEnum.SLIDER) {
                    promoCarouselsData.push(carouselData);
                }
                if (
                    [
                        PageItemTypeEnum.SINGLE_ROW,
                        PageItemTypeEnum.SINGLE_HIGHLIGHT,
                        PageItemTypeEnum.DOUBLE_ROW
                    ].includes(carouselData.type)
                ) {
                    carouselData.playlistId = pageItem.data.playlistId;
                    singleRowCarouselsData.push(carouselData);
                }
            }
        });
        return [promoCarouselsData, singleRowCarouselsData];
    };
    const [promoCarouselsData, singleRowCarouselsData] = getCarouselData(pageItems);

    const displayFilter = route.params?.displayPageFilter || undefined;
    const shouldUsePackageFilter = route.params?.shouldUsePackageFilter || undefined;
    const hasPackageFilter = Boolean(filterOptions);

    if (route.params?.pageOptions?.slug === 'special_canalplus_1') {
        if (canalPlusAppInstalled === false && canalPlusWizard === null) {
            getCanalPlus();
            setCanalPlusWizard('pending');
        }

        // canal plus dependencies loader
        if (
            canalPlusAppInstalled === null ||
            (canalPlusAppInstalled === false &&
                (canalPlusWizard === 'pending' || canalPlusWizard === null))
        ) {
            return <ContentWrapper loading />;
        }

        // return wizard
        if (canalPlusAppInstalled === false) {
            return <Wizard data={canalPlusWizard} />;
        }
    }

    const DATA = [];

    DATA.push(
        ...promoCarouselsData.map((carousel, key) => (
            <View key={`promocarousel_${key}`}>
                <PromoCarousel
                    key={carousel.key}
                    testID={carousel.testId}
                    style={defaultStyles.promoCarousel}
                    navigation={navigation}
                    items={carousel.items || []}
                    onItemPress={onItemPress}
                    hideImages={pageIsBlurred}
                />
            </View>
        ))
    );

    if (showTvGuideButton) {
        DATA.push(
            <Button
                style={{
                    button: defaultStyles.tvGuideButton
                }}
                // key="tvGuideButton"
                testID="tvGuideButton"
                title={t('TV.GUIDE')}
                iconName="tv_guide"
                onPress={() => {
                    navigation.navigate('TvGuide');
                    logEvent({
                        type: 'user-flow',
                        subtype: 'home',
                        content1: 'open-epg-button',
                        content2: '',
                        context: '/epg'
                    });
                }}
            />
        );
    }

    DATA.push(
        singleRowCarouselsData.map((carousel, index) => {
            const displayItems =
                !shouldUsePackageFilter || packageFilter === 0
                    ? carousel.items
                    : carousel.items.filter((item) => item.data?.pckg?.id === packageFilter);

            if (displayItems.length > 0) {
                return (
                    <Carousel
                        key={`carousel_${index}`}
                        testID={carousel.testId}
                        style={[defaultStyles.carousel, { flex: 1 }]}
                        title={Playlist.replaceLabelTags(carousel.title)}
                        items={displayItems || []}
                        onViewAllPress={(title, showMetadata, playlistUrl) => {
                            onViewAllPress(title, showMetadata, playlistUrl);
                            logEvent({
                                type: 'user-flow',
                                subtype: navigation.getState().routes[0].name,
                                content1: 'carousel-show-more',
                                content2: carousel.title,
                                context: '/show-all'
                            });
                        }}
                        playlistUrl={carousel.playlistId}
                        onItemPress={(item) => {
                            onItemPress(item);
                            logEvent({
                                type: 'user-flow',
                                subtype: navigation.getState().routes[0].name,
                                content1: 'carousel-open-detail',
                                content2: carousel.title,
                                context: '/detail'
                            });
                        }}
                        // hideImages={pageIsBlurred}
                        forcePortraitViewPackshotDimensions={Device.isPhone}
                        forceHideGradient={carousel.dataType === 'continue-watching'}
                    />
                );
            }
            return null;
        })
    );

    //** Temporary fix for videostore=> to the future necessary create beter solution */
    const navData = navigation.getParent().getState();
    const implemementTopSpacing = useMemo(() => {
        const dataLength = navData.routes.length;
        const showSpaceForVideostore = navData.routes[dataLength - 1].name === 'videostore';
        return showSpaceForVideostore;
    }, [navData]);

    return (
        <Freeze freeze={pageIsBlurred}>
            <ContentWrapper
                noScrollView
                refreshControl={
                    <RefreshControl
                        colors={[Theme.primaryActiveColor]}
                        tintColor={Theme.primaryActiveColor}
                        refreshing={refreshing}
                        onRefresh={onRefresh}
                    />
                }
                loading={!loaded}
            >
                {shouldUsePackageFilter && hasPackageFilter && (
                    <PackageFilter
                        displayFilters={displayFilter}
                        options={filterOptions}
                        selectedOption={packageFilter}
                        onClose={hideFilter}
                        onSelect={onPackageFilterSelect}
                        loading={!loaded}
                    />
                )}
                <FlashList
                    onScroll={(e) => dispatch(handleScroll(e))}
                    onContentSizeChange={(_w, h) =>
                        setContentHeight(implemementTopSpacing ? h + DETAIL_HEADER_HEIGHT : h)
                    }
                    data={DATA}
                    renderItem={renderItem}
                    estimatedItemSize={200}
                    drawDistance={1}
                    contentContainerStyle={defaultStyles.container}
                    ListHeaderComponent={
                        implemementTopSpacing
                            ? () => <View style={{ height: DETAIL_HEADER_HEIGHT }} />
                            : null
                    }
                    ListHeaderComponentStyle={{ width: '100%' }}
                    ListFooterComponent={isLongContent ? FooterComponent : null}
                    ListFooterComponentStyle={{ width: '100%' }}
                />
                {!isLongContent && <View style={{ width: '100%' }}>{FooterComponent}</View>}
            </ContentWrapper>
        </Freeze>
    );
};

Page.propTypes = {
    navigation: PropTypes.shape({}).isRequired
};

export default withConfirmationModal(Page);
